import { useState, useEffect, useCallback, useRef } from "react";
import { AdminLayout } from "@/components/shared/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getAllPages, updatePageContent, createPageContent, deletePageContent, getLocations, autoFitHeroImages, uploadCleanAndFitHero } from "@/lib/api";
import { ImageUpload, MultiImageUpload, VideoUpload } from "@/components/shared/ImageUpload";
import { toast } from "sonner";
import {
  FileText, Plus, Trash2, Edit, Loader2, Layout, Eye, Save, Palette, ChevronDown,
  ArrowUp, ArrowDown, Copy, HelpCircle, FolderPlus, Pencil, GripVertical,
  ChevronRight,
  CreditCard, Smartphone, Wifi, Shield, Settings, Users, MapPin,
  Phone, Zap, Globe, Package, Headphones, BarChart3,
  Lock, Star, Truck, MessageCircle, Wand2, Sparkles,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  getFaqCategories, createFaqCategory, updateFaqCategory, deleteFaqCategory,
  getFaqItems, createFaqItem, updateFaqItem, deleteFaqItem,
  getCareerPositions, createCareerPosition, updateCareerPosition, deleteCareerPosition,
  bulkGeneratePositions, deleteAllCareerPositions, bulkUpdatePositionsByType,
} from "@/lib/api";

const COLOR_PRESETS = [
  { label: "White", value: "#ffffff" },
  { label: "Navy", value: "#0B1B3D" },
  { label: "Teal", value: "#00B2A9" },
  { label: "Slate", value: "#475569" },
  { label: "Black", value: "#000000" },
  { label: "Red", value: "#dc2626" },
];

function FontColorEditor({ colors = {}, onChange }) {
  const [expanded, setExpanded] = useState(false);
  const update = (key, val) => onChange({ ...colors, [key]: val });

  const ColorField = ({ label, field, defaultVal }) => (
    <div className="space-y-1">
      <Label className="text-xs text-slate-500">{label}</Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={colors[field] || defaultVal || "#000000"}
          onChange={(e) => update(field, e.target.value)}
          className="w-8 h-8 rounded border cursor-pointer p-0"
          data-testid={`color-${field}`}
        />
        <Input
          value={colors[field] || ""}
          onChange={(e) => update(field, e.target.value)}
          placeholder={defaultVal || "Default"}
          className="text-xs h-8 flex-1 font-mono"
          data-testid={`color-${field}-input`}
        />
        {colors[field] && (
          <button type="button" onClick={() => { const c = { ...colors }; delete c[field]; onChange(c); }} className="text-slate-400 hover:text-red-500 text-xs" title="Reset to default">Reset</button>
        )}
      </div>
      <div className="flex gap-1">
        {COLOR_PRESETS.map((p) => (
          <button key={p.value} type="button" onClick={() => update(field, p.value)} className="w-5 h-5 rounded-full border border-slate-300 hover:scale-110 transition-transform" style={{ backgroundColor: p.value }} title={p.label} />
        ))}
      </div>
    </div>
  );

  return (
    <div className="border-t pt-3" data-testid="font-color-editor">
      <button type="button" onClick={() => setExpanded(!expanded)} className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-slate-900 w-full">
        <Palette className="w-4 h-4" />
        Font Colors
        <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>
      {expanded && (
        <div className="grid grid-cols-2 gap-3 mt-3">
          <ColorField label="Title Color" field="title_color" defaultVal="#0B1B3D" />
          <ColorField label="Body Text Color" field="text_color" defaultVal="#475569" />
          <ColorField label="Accent / Highlight Color" field="accent_color" defaultVal="#00B2A9" />
          <ColorField label="Subtitle / Label Color" field="label_color" defaultVal="#00B2A9" />
        </div>
      )}
    </div>
  );
}

const pagesList = [
  { slug: "home", name: "Home Page" },
  { slug: "about", name: "About Page" },
  { slug: "services", name: "Services Page" },
  { slug: "our-process", name: "Our Process" },
  { slug: "locations", name: "Locations Page" },
  { slug: "contact", name: "Contact Page" },
  { slug: "become-dealer", name: "Become a Dealer" },
  { slug: "submit-location", name: "Submit Location Form" },
  { slug: "header", name: "Header / Nav" },
  { slug: "footer", name: "Footer" },
  { slug: "faqs", name: "FAQs" },
  { slug: "careers", name: "Careers" },
  { slug: "twma", name: "TWMA" },
];

// Section definitions per page
const PAGE_SECTIONS = {
  home: {
    hero: { label: "Hero Section", fields: ["title", "content", "image_url", "button_text", "button_link"], metaFields: ["subtitle", "highlight"] },
    stats: { label: "Stats Section", fields: ["title"], listType: "stats" },
    features: { label: "Why Choose Us", fields: ["title", "content"], listType: "features", metaFields: ["subtitle"] },
    benefits: { label: "Benefits Section", fields: ["title", "content", "image_url", "button_text", "button_link"], listType: "benefits", metaFields: ["subtitle"] },
    products: { label: "Products Section", fields: ["title", "content"], listType: "features", metaFields: ["subtitle"] },
    cta: { label: "Call to Action", fields: ["title", "content", "button_text", "button_link"], metaFields: ["secondary_button_text", "secondary_button_link"] },
  },
  about: {
    hero: { label: "Hero Section", fields: ["title", "content"], metaFields: ["subtitle"] },
    story: { label: "Our Story", fields: ["title", "content", "image_url"] },
    mission_vision: { label: "Mission & Vision", fields: ["title"], listType: "features" },
    values: { label: "Core Values", fields: ["title", "content"], listType: "features" },
    team: { label: "Leadership Team", fields: ["title", "content"], listType: "team" },
  },
  services: {
    hero: { label: "Hero Section", fields: ["title", "content"], metaFields: ["subtitle"] },
    plans: { label: "Wireless Plans", fields: ["title", "content"], listType: "plans", metaFields: ["subtitle", "disclaimer"] },
    prime_media_wireless_services: { label: "PMW Services", fields: ["title", "content"], listType: "pmw_services", metaFields: ["subtitle", "disclaimer"] },
    network: { label: "Network Benefits", fields: ["title", "content", "image_url"], listType: "features", metaFields: ["subtitle"] },
    devices: { label: "Devices & Accessories", fields: ["title", "content"], listType: "devices", metaFields: ["subtitle"] },
    cta: { label: "Call to Action", fields: ["title", "content", "button_text", "button_link"] },
  },
  "our-process": {
    hero: { label: "Hero Section", fields: ["title", "content"], metaFields: ["subtitle"] },
    steps: { label: "Process Steps", fields: ["title", "content"], listType: "features", metaFields: ["subtitle"] },
    benefits: { label: "Why Dealers Choose Us", fields: ["title"], listType: "features" },
    cta: { label: "Call to Action", fields: ["title", "content", "button_text", "button_link"] },
  },
  locations: {
    hero: { label: "Hero Section", fields: ["title", "content"], metaFields: ["subtitle"] },
    cta: { label: "Call to Action", fields: ["title", "content", "button_text", "button_link"] },
  },
  contact: {
    hero: { label: "Hero Section", fields: ["title", "content"], metaFields: ["subtitle"] },
    contact_info: { label: "Contact Information", fields: ["title"], listType: "contact_items" },
    form: { label: "Form Section", fields: ["title", "content"], listType: "form_fields" },
  },
  "become-dealer": {
    hero: { label: "Hero Section", fields: ["title", "content"], metaFields: ["subtitle"] },
    requirements: { label: "Requirements", fields: ["title", "content"], listType: "benefits", metaFields: ["contact_email", "contact_phone"], hasFormFields: true },
  },
  "submit-location": {
    form: { label: "Form Section", fields: ["title", "content"], listType: "form_fields" },
  },
  header: {
    nav_links: { label: "Navigation Links", fields: ["title"], listType: "nav_links" },
    phone: { label: "Phone Number", fields: ["content", "button_link"] },
  },
  footer: {
    brand: { label: "Brand / Description", fields: ["title", "content"] },
    quick_links: { label: "Quick Links Column", fields: ["title"], listType: "nav_links" },
    useful_links: { label: "Useful Links Column", fields: ["title"], listType: "nav_links" },
    contact_info: { label: "Contact Information", fields: ["title"], listType: "nav_links" },
  },
  careers: {
    hero: { label: "Hero Section", fields: ["title", "content"], metaFields: ["subtitle"] },
    benefits: { label: "Benefits Section", fields: ["title"], listType: "features" },
    cta: { label: "Bottom CTA", fields: ["title", "content", "button_text", "button_link"] },
  },
  twma: {
    hero: { label: "Hero Section", fields: ["title", "content", "button_text", "button_link"], metaFields: ["subtitle", "secondary_button_text", "secondary_button_link"] },
    about: { label: "What is a Total Wireless Master Agent?", fields: ["title", "content"] },
    benefits: { label: "Why Partner With PMW", fields: ["title", "content"], listType: "features", metaFields: ["subtitle"] },
    incentives: { label: "Dealer Incentives", fields: ["title"], listType: "stats" },
    what_we_provide: { label: "What We Provide", fields: ["title"], listType: "features" },
    responsibilities: { label: "Dealer Responsibilities", fields: ["title"], listType: "features" },
    states: { label: "Available States Section", fields: ["title", "content"] },
    faqs: { label: "FAQs", fields: ["title"], listType: "features" },
    cta: { label: "Bottom CTA", fields: ["title", "content", "button_text", "button_link"], metaFields: ["secondary_button_text", "secondary_button_link"] },
  },
};

// ===== Editor Components =====
function StatsEditor({ items, onChange }) {
  const update = (idx, field, value) => { const n = [...items]; n[idx] = { ...n[idx], [field]: value }; onChange(n); };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const move = (idx, dir) => { const n = [...items]; [n[idx], n[idx + dir]] = [n[idx + dir], n[idx]]; onChange(n); };
  return (
    <div className="space-y-2">
      <Label className="text-sm font-semibold">Stats Items</Label>
      {items.map((item, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <div className="flex flex-col gap-0.5">
            <Button type="button" size="icon" variant="ghost" className="h-5 w-5 text-slate-400 hover:text-slate-700" onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`stat-up-${idx}`}><ArrowUp className="w-3 h-3" /></Button>
            <Button type="button" size="icon" variant="ghost" className="h-5 w-5 text-slate-400 hover:text-slate-700" onClick={() => move(idx, 1)} disabled={idx === items.length - 1} data-testid={`stat-down-${idx}`}><ArrowDown className="w-3 h-3" /></Button>
          </div>
          <Input placeholder="Value (e.g. 500+)" value={item.value || ""} onChange={(e) => update(idx, "value", e.target.value)} className="w-32" data-testid={`stat-value-${idx}`} />
          <Input placeholder="Label (e.g. Active Dealers)" value={item.label || ""} onChange={(e) => update(idx, "label", e.target.value)} className="flex-1" data-testid={`stat-label-${idx}`} />
          <Button type="button" size="icon" variant="ghost" className="text-red-500 h-8 w-8" onClick={() => remove(idx)}><Trash2 className="w-4 h-4" /></Button>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={() => onChange([...items, { value: "", label: "" }])} data-testid="add-stat-btn"><Plus className="w-3 h-3 mr-1" /> Add Stat</Button>
    </div>
  );
}

function FeaturesEditor({ items, onChange, label = "Items" }) {
  const update = (idx, field, value) => { const n = [...items]; n[idx] = { ...n[idx], [field]: value }; onChange(n); };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  return (
    <div className="space-y-3">
      <Label className="text-sm font-semibold">{label}</Label>
      {items.map((item, idx) => (
        <div key={idx} className="border rounded-md p-3 bg-slate-50 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400">Item {idx + 1}</span>
            <Button type="button" size="icon" variant="ghost" className="text-red-500 h-6 w-6" onClick={() => remove(idx)}><Trash2 className="w-3 h-3" /></Button>
          </div>
          <Input placeholder="Icon name (e.g. Signal, DollarSign)" value={item.icon || ""} onChange={(e) => update(idx, "icon", e.target.value)} className="text-sm" data-testid={`feature-icon-${idx}`} />
          <Input placeholder="Title" value={item.title || ""} onChange={(e) => update(idx, "title", e.target.value)} className="text-sm" data-testid={`feature-title-${idx}`} />
          <Textarea placeholder="Description" value={item.description || ""} onChange={(e) => update(idx, "description", e.target.value)} rows={2} className="text-sm" data-testid={`feature-desc-${idx}`} />
          <ImageUpload value={item.image_url || ""} onChange={(url) => update(idx, "image_url", url)} label={`Card Image ${idx + 1}`} folder="images" testId={`feature-image-${idx}`} />
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={() => onChange([...items, { icon: "", title: "", description: "", image_url: "" }])} data-testid="add-feature-btn"><Plus className="w-3 h-3 mr-1" /> Add Item</Button>
    </div>
  );
}

function BenefitsEditor({ items, onChange }) {
  const update = (idx, value) => { const n = [...items]; n[idx] = value; onChange(n); };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  return (
    <div className="space-y-2">
      <Label className="text-sm font-semibold">List Items</Label>
      {items.map((item, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <Input placeholder="Item text..." value={item || ""} onChange={(e) => update(idx, e.target.value)} className="flex-1" data-testid={`benefit-item-${idx}`} />
          <Button type="button" size="icon" variant="ghost" className="text-red-500 h-8 w-8" onClick={() => remove(idx)}><Trash2 className="w-3 h-3" /></Button>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={() => onChange([...items, ""])} data-testid="add-benefit-btn"><Plus className="w-3 h-3 mr-1" /> Add Item</Button>
    </div>
  );
}

function TeamEditor({ items, onChange }) {
  const update = (idx, field, value) => { const n = [...items]; n[idx] = { ...n[idx], [field]: value }; onChange(n); };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  return (
    <div className="space-y-3">
      <Label className="text-sm font-semibold">Team Members</Label>
      {items.map((item, idx) => (
        <div key={idx} className="border rounded-md p-3 bg-slate-50 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400">Member {idx + 1}</span>
            <Button type="button" size="icon" variant="ghost" className="text-red-500 h-6 w-6" onClick={() => remove(idx)}><Trash2 className="w-3 h-3" /></Button>
          </div>
          <Input placeholder="Full Name" value={item.name || ""} onChange={(e) => update(idx, "name", e.target.value)} data-testid={`team-name-${idx}`} />
          <Input placeholder="Role / Title" value={item.role || ""} onChange={(e) => update(idx, "role", e.target.value)} data-testid={`team-role-${idx}`} />
          <Input placeholder="Years / Experience" value={item.years || ""} onChange={(e) => update(idx, "years", e.target.value)} data-testid={`team-years-${idx}`} />
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={() => onChange([...items, { name: "", role: "", years: "" }])} data-testid="add-team-btn"><Plus className="w-3 h-3 mr-1" /> Add Member</Button>
    </div>
  );
}

function PlansEditor({ items, onChange }) {
  const update = (idx, field, value) => { const n = [...items]; n[idx] = { ...n[idx], [field]: value }; onChange(n); };
  const updateFeature = (pIdx, fIdx, value) => {
    const n = [...items]; const features = [...(n[pIdx].features || [])]; features[fIdx] = value; n[pIdx] = { ...n[pIdx], features }; onChange(n);
  };
  const addFeature = (pIdx) => { const n = [...items]; n[pIdx] = { ...n[pIdx], features: [...(n[pIdx].features || []), ""] }; onChange(n); };
  const removeFeature = (pIdx, fIdx) => { const n = [...items]; n[pIdx] = { ...n[pIdx], features: n[pIdx].features.filter((_, i) => i !== fIdx) }; onChange(n); };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  return (
    <div className="space-y-3">
      {items.map((plan, idx) => (
        <div key={idx} className="border rounded-md p-3 bg-slate-50 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400">Plan {idx + 1}</span>
            <Button type="button" size="icon" variant="ghost" className="text-red-500 h-6 w-6" onClick={() => remove(idx)}><Trash2 className="w-3 h-3" /></Button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Input placeholder="Name" value={plan.name || ""} onChange={(e) => update(idx, "name", e.target.value)} data-testid={`plan-name-${idx}`} />
            <Input placeholder="Data (e.g. 15GB)" value={plan.data || ""} onChange={(e) => update(idx, "data", e.target.value)} data-testid={`plan-data-${idx}`} />
            <Input placeholder="Price (e.g. $30)" value={plan.price || ""} onChange={(e) => update(idx, "price", e.target.value)} data-testid={`plan-price-${idx}`} />
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={plan.popular || false} onChange={(e) => update(idx, "popular", e.target.checked)} /> Most Popular</label>
          <div className="space-y-1 pl-2">
            <Label className="text-xs">Features</Label>
            {(plan.features || []).map((f, fIdx) => (
              <div key={fIdx} className="flex items-center gap-1">
                <Input value={f} onChange={(e) => updateFeature(idx, fIdx, e.target.value)} className="text-xs h-7" placeholder="Feature..." />
                <Button type="button" size="icon" variant="ghost" className="text-red-400 h-6 w-6" onClick={() => removeFeature(idx, fIdx)}><Trash2 className="w-3 h-3" /></Button>
              </div>
            ))}
            <Button type="button" size="sm" variant="ghost" className="text-xs h-6" onClick={() => addFeature(idx)}><Plus className="w-3 h-3 mr-1" /> Feature</Button>
          </div>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={() => onChange([...items, { name: "", data: "", price: "", popular: false, features: [] }])} data-testid="add-plan-btn"><Plus className="w-3 h-3 mr-1" /> Add Plan</Button>
    </div>
  );
}

const PLAN_CATS = [
  { key: "items", label: "Phone Plans" },
  { key: "tablet_items", label: "Tablet Plans" },
  { key: "home_internet_items", label: "Home Internet" },
  { key: "hotspot_items", label: "Mobile Hotspot" },
];

function PlansCategoryEditor({ allPlans, onChange }) {
  const [activeCat, setActiveCat] = useState("items");

  const handleCatChange = (key, newItems) => {
    onChange({ ...allPlans, [key]: newItems });
  };

  return (
    <div className="space-y-3">
      <Label className="text-sm font-semibold">Plans by Category</Label>
      <div className="flex flex-wrap gap-1 border-b pb-2">
        {PLAN_CATS.map((cat) => (
          <button
            key={cat.key}
            type="button"
            onClick={() => setActiveCat(cat.key)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              activeCat === cat.key
                ? "bg-navy text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
            data-testid={`plan-cat-tab-${cat.key}`}
          >
            {cat.label} ({(allPlans[cat.key] || []).length})
          </button>
        ))}
      </div>
      <PlansEditor
        items={allPlans[activeCat] || []}
        onChange={(newItems) => handleCatChange(activeCat, newItems)}
      />
    </div>
  );
}

const PMW_CATS = [
  { key: "devices_items", label: "Devices" },
  { key: "accessories_items", label: "Accessories" },
  { key: "electronic_parts_items", label: "Electronic Parts" },
  { key: "training_items", label: "Training & Support" },
];

function PmwServicesCategoryEditor({ allPmw, onChange }) {
  const [activeCat, setActiveCat] = useState("devices_items");

  const handleCatChange = (key, newItems) => {
    onChange({ ...allPmw, [key]: newItems });
  };

  return (
    <div className="space-y-3">
      <Label className="text-sm font-semibold">PMW Services by Category</Label>
      <div className="flex flex-wrap gap-1 border-b pb-2">
        {PMW_CATS.map((cat) => (
          <button
            key={cat.key}
            type="button"
            onClick={() => setActiveCat(cat.key)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              activeCat === cat.key
                ? "bg-navy text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
            data-testid={`pmw-cat-tab-${cat.key}`}
          >
            {cat.label} ({(allPmw[cat.key] || []).length})
          </button>
        ))}
      </div>
      <PlansEditor
        items={allPmw[activeCat] || []}
        onChange={(newItems) => handleCatChange(activeCat, newItems)}
      />
    </div>
  );
}

function DevicesEditor({ items, onChange }) {
  const update = (idx, field, value) => { const n = [...items]; n[idx] = { ...n[idx], [field]: value }; onChange(n); };
  const updateBrand = (dIdx, bIdx, value) => {
    const n = [...items]; const brands = [...(n[dIdx].brands || [])]; brands[bIdx] = value; n[dIdx] = { ...n[dIdx], brands }; onChange(n);
  };
  const addBrand = (dIdx) => { const n = [...items]; n[dIdx] = { ...n[dIdx], brands: [...(n[dIdx].brands || []), ""] }; onChange(n); };
  const removeBrand = (dIdx, bIdx) => { const n = [...items]; n[dIdx] = { ...n[dIdx], brands: n[dIdx].brands.filter((_, i) => i !== bIdx) }; onChange(n); };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  return (
    <div className="space-y-3">
      <Label className="text-sm font-semibold">Device Categories</Label>
      {items.map((item, idx) => (
        <div key={idx} className="border rounded-md p-3 bg-slate-50 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400">Category {idx + 1}</span>
            <Button type="button" size="icon" variant="ghost" className="text-red-500 h-6 w-6" onClick={() => remove(idx)}><Trash2 className="w-3 h-3" /></Button>
          </div>
          <Input placeholder="Icon (e.g. Smartphone)" value={item.icon || ""} onChange={(e) => update(idx, "icon", e.target.value)} className="text-sm" />
          <Input placeholder="Title" value={item.title || ""} onChange={(e) => update(idx, "title", e.target.value)} className="text-sm" />
          <Textarea placeholder="Description" value={item.description || ""} onChange={(e) => update(idx, "description", e.target.value)} rows={2} className="text-sm" />
          <div className="space-y-1 pl-2">
            <Label className="text-xs">Brands / Items</Label>
            {(item.brands || []).map((b, bIdx) => (
              <div key={bIdx} className="flex items-center gap-1">
                <Input value={b} onChange={(e) => updateBrand(idx, bIdx, e.target.value)} className="text-xs h-7" />
                <Button type="button" size="icon" variant="ghost" className="text-red-400 h-6 w-6" onClick={() => removeBrand(idx, bIdx)}><Trash2 className="w-3 h-3" /></Button>
              </div>
            ))}
            <Button type="button" size="sm" variant="ghost" className="text-xs h-6" onClick={() => addBrand(idx)}><Plus className="w-3 h-3 mr-1" /> Brand</Button>
          </div>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={() => onChange([...items, { icon: "", title: "", description: "", brands: [] }])}><Plus className="w-3 h-3 mr-1" /> Add Category</Button>
    </div>
  );
}

function ContactItemsEditor({ items, onChange }) {
  const update = (idx, field, value) => { const n = [...items]; n[idx] = { ...n[idx], [field]: value }; onChange(n); };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  return (
    <div className="space-y-3">
      <Label className="text-sm font-semibold">Contact Items</Label>
      {items.map((item, idx) => (
        <div key={idx} className="border rounded-md p-3 bg-slate-50 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400">Item {idx + 1}</span>
            <Button type="button" size="icon" variant="ghost" className="text-red-500 h-6 w-6" onClick={() => remove(idx)}><Trash2 className="w-3 h-3" /></Button>
          </div>
          <Input placeholder="Label (e.g. Phone, Email)" value={item.label || ""} onChange={(e) => update(idx, "label", e.target.value)} data-testid={`contact-label-${idx}`} />
          <Textarea placeholder="Value (e.g. 1-800-PRIME-MW)" value={item.value || ""} onChange={(e) => update(idx, "value", e.target.value)} rows={2} data-testid={`contact-value-${idx}`} />
          <Input placeholder="Link (e.g. tel:, mailto:) - leave empty if none" value={item.href || ""} onChange={(e) => update(idx, "href", e.target.value)} data-testid={`contact-href-${idx}`} />
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={() => onChange([...items, { label: "", value: "", href: "" }])} data-testid="add-contact-btn"><Plus className="w-3 h-3 mr-1" /> Add Item</Button>
    </div>
  );
}

function FormFieldsEditor({ items, onChange }) {
  const update = (idx, field, value) => { const n = [...items]; n[idx] = { ...n[idx], [field]: value }; onChange(n); };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  return (
    <div className="space-y-3">
      <Label className="text-sm font-semibold">Form Fields</Label>
      {items.map((item, idx) => (
        <div key={idx} className="border rounded-md p-3 bg-slate-50 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400">Field {idx + 1}</span>
            <Button type="button" size="icon" variant="ghost" className="text-red-500 h-6 w-6" onClick={() => remove(idx)}><Trash2 className="w-3 h-3" /></Button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Input placeholder="Field name (e.g. email)" value={item.name || ""} onChange={(e) => update(idx, "name", e.target.value)} className="text-sm" />
            <Input placeholder="Label (e.g. Email)" value={item.label || ""} onChange={(e) => update(idx, "label", e.target.value)} className="text-sm" />
            <select className="border rounded-md px-2 text-sm h-9" value={item.type || "text"} onChange={(e) => update(idx, "type", e.target.value)}>
              <option value="text">Text</option><option value="email">Email</option><option value="tel">Phone</option><option value="number">Number</option><option value="textarea">Textarea</option><option value="select">Dropdown</option>
            </select>
          </div>
          <div className="flex items-center gap-3">
            <Input placeholder="Placeholder text" value={item.placeholder || ""} onChange={(e) => update(idx, "placeholder", e.target.value)} className="text-sm flex-1" />
            <label className="flex items-center gap-1 text-xs whitespace-nowrap"><input type="checkbox" checked={item.required || false} onChange={(e) => update(idx, "required", e.target.checked)} /> Required</label>
          </div>
          {item.type === "select" && (
            <OptionsInput options={item.options || []} onChange={(opts) => update(idx, "options", opts)} />
          )}
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={() => onChange([...items, { name: "", label: "", type: "text", required: false }])} data-testid="add-form-field-btn"><Plus className="w-3 h-3 mr-1" /> Add Field</Button>
    </div>
  );
}

// One option per line so values like "$50,000 - $100,000" keep their commas
function OptionsInput({ options, onChange }) {
  const [text, setText] = useState(options.join("\n"));
  return (
    <textarea rows={Math.max(2, options.length)} placeholder="Options (one per line)" value={text} data-testid="form-field-options"
      onChange={(e) => setText(e.target.value)}
      onBlur={() => onChange(text.split("\n").map((s) => s.trim()).filter(Boolean))}
      className="w-full border rounded-md px-2 py-1 text-sm" />
  );
}

function NavLinksEditor({ items, onChange }) {
  const update = (idx, field, value) => { const n = [...items]; n[idx] = { ...n[idx], [field]: value }; onChange(n); };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const moveUp = (idx) => { if (idx === 0) return; const n = [...items]; [n[idx - 1], n[idx]] = [n[idx], n[idx - 1]]; onChange(n); };
  const moveDown = (idx) => { if (idx === items.length - 1) return; const n = [...items]; [n[idx], n[idx + 1]] = [n[idx + 1], n[idx]]; onChange(n); };
  return (
    <div className="space-y-2">
      <Label className="text-sm font-semibold">Links</Label>
      {items.map((item, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <span className="text-xs text-slate-400 w-5 text-center">{idx + 1}</span>
          <Input placeholder="Label (e.g. About Us)" value={item.title || ""} onChange={(e) => update(idx, "title", e.target.value)} className="flex-1" data-testid={`navlink-title-${idx}`} />
          <Input placeholder="Path (e.g. /about)" value={item.description || ""} onChange={(e) => update(idx, "description", e.target.value)} className="w-40" data-testid={`navlink-path-${idx}`} />
          <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => moveUp(idx)} disabled={idx === 0} title="Move up"><span className="text-xs">&#9650;</span></Button>
          <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => moveDown(idx)} disabled={idx === items.length - 1} title="Move down"><span className="text-xs">&#9660;</span></Button>
          <Button type="button" size="icon" variant="ghost" className="text-red-500 h-8 w-8" onClick={() => remove(idx)}><Trash2 className="w-3 h-3" /></Button>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={() => onChange([...items, { title: "", description: "" }])} data-testid="add-navlink-btn"><Plus className="w-3 h-3 mr-1" /> Add Link</Button>
    </div>
  );
}

// ===== Section Editor Dialog =====
function SectionEditorDialog({ section, sectionDef, open, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [meta, setMeta] = useState({});
  const [listItems, setListItems] = useState([]);
  const [formFields, setFormFields] = useState([]);
  const [allPlans, setAllPlans] = useState({ items: [], tablet_items: [], home_internet_items: [], hotspot_items: [] });
  const [allPmw, setAllPmw] = useState({ devices_items: [], accessories_items: [], electronic_parts_items: [], training_items: [] });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (section) {
      setForm({ title: section.title || "", content: section.content || "", image_url: section.image_url || "", video_url: section.video_url || "", button_text: section.button_text || "", button_link: section.button_link || "", active: section.active !== false });
      const m = section.metadata || {};
      setMeta(m);
      setListItems(m.items || []);
      setFormFields(m.fields || m.form_fields || []);
      if (sectionDef?.listType === "plans") {
        setAllPlans({
          items: m.items || [],
          tablet_items: m.tablet_items || [],
          home_internet_items: m.home_internet_items || [],
          hotspot_items: m.hotspot_items || [],
        });
      }
      if (sectionDef?.listType === "pmw_services") {
        setAllPmw({
          devices_items: m.devices_items || [],
          accessories_items: m.accessories_items || [],
          electronic_parts_items: m.electronic_parts_items || [],
          training_items: m.training_items || [],
        });
      }
    }
  }, [section, sectionDef]);

  const handleSave = async () => {
    setSubmitting(true);
    try {
      const metadata = { ...meta };
      if (sectionDef?.listType === "plans") {
        metadata.items = allPlans.items;
        metadata.tablet_items = allPlans.tablet_items;
        metadata.home_internet_items = allPlans.home_internet_items;
        metadata.hotspot_items = allPlans.hotspot_items;
      } else if (sectionDef?.listType === "pmw_services") {
        metadata.devices_items = allPmw.devices_items;
        metadata.accessories_items = allPmw.accessories_items;
        metadata.electronic_parts_items = allPmw.electronic_parts_items;
        metadata.training_items = allPmw.training_items;
      } else if (sectionDef?.listType) {
        metadata.items = listItems;
      }
      if (sectionDef?.listType === "form_fields") { metadata.fields = formFields; delete metadata.items; }
      if (sectionDef?.hasFormFields) metadata.form_fields = formFields;
      await updatePageContent(section.id, { ...form, page_slug: section.page_slug, section_name: section.section_name, order: section.order || 0, metadata });
      toast.success("Section updated");
      onSaved();
      onClose();
    } catch (e) { toast.error("Error saving section"); }
    finally { setSubmitting(false); }
  };

  const fieldDefs = sectionDef?.fields || [];
  const metaFieldDefs = sectionDef?.metaFields || [];
  const lt = sectionDef?.listType;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Edit className="w-5 h-5" /> Edit: {sectionDef?.label || section?.section_name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {metaFieldDefs.map((mf) => (
            <div key={mf}><Label className="capitalize">{mf.replace(/_/g, " ")}</Label><Input value={meta[mf] || ""} onChange={(e) => setMeta({ ...meta, [mf]: e.target.value })} data-testid={`meta-${mf}`} /></div>
          ))}
          {fieldDefs.includes("title") && <div><Label>Title</Label><Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="section-title" /></div>}
          {fieldDefs.includes("content") && <div><Label>Content / Description</Label><Textarea value={form.content || ""} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={3} data-testid="section-content" /></div>}
          {fieldDefs.includes("image_url") && <div><Label>Image</Label><ImageUpload value={form.image_url || ""} onChange={(url) => setForm({ ...form, image_url: url })} label="Section Image" folder="images" testId="section-image" /></div>}
          {fieldDefs.includes("video_url") && <div><Label>Video (MP4)</Label><VideoUpload value={form.video_url || ""} onChange={(url) => setForm({ ...form, video_url: url })} label="Section Video" folder="videos" testId="section-video" /><p className="text-xs text-slate-500 mt-1">Upload an MP4 video. When set, this replaces the image on the public page.</p></div>}
          {fieldDefs.includes("button_text") && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Button Text</Label><Input value={form.button_text || ""} onChange={(e) => setForm({ ...form, button_text: e.target.value })} data-testid="section-btn-text" /></div>
              <div><Label>Button Link</Label><Input value={form.button_link || ""} onChange={(e) => setForm({ ...form, button_link: e.target.value })} data-testid="section-btn-link" /></div>
            </div>
          )}
          {lt === "nav_links" && <NavLinksEditor items={listItems} onChange={setListItems} />}
          {lt === "stats" && <StatsEditor items={listItems} onChange={setListItems} />}
          {lt === "features" && <FeaturesEditor items={listItems} onChange={setListItems} />}
          {lt === "benefits" && <BenefitsEditor items={listItems} onChange={setListItems} />}
          {lt === "team" && <TeamEditor items={listItems} onChange={setListItems} />}
          {lt === "plans" && <PlansCategoryEditor allPlans={allPlans} onChange={setAllPlans} />}
          {lt === "pmw_services" && <PmwServicesCategoryEditor allPmw={allPmw} onChange={setAllPmw} />}
          {lt === "devices" && <DevicesEditor items={listItems} onChange={setListItems} />}
          {lt === "contact_items" && <ContactItemsEditor items={listItems} onChange={setListItems} />}
          {lt === "form_fields" && <FormFieldsEditor items={formFields} onChange={setFormFields} />}
          {sectionDef?.hasFormFields && <FormFieldsEditor items={formFields} onChange={setFormFields} />}
          {section?.section_name === "story" && section?.page_slug === "about" && (
            <div className="border-t pt-3">
              <MultiImageUpload images={meta.carousel_images || []} onChange={(imgs) => setMeta({ ...meta, carousel_images: imgs })} max={5} label="Rotating Images (up to 5)" folder="images" testId="story-carousel" />
            </div>
          )}
          {section?.section_name === "hero" && section?.page_slug === "home" && (
            <div className="border-t pt-3">
              <MultiImageUpload
                images={meta.carousel_images || []}
                onChange={(imgs) => setMeta({ ...meta, carousel_images: imgs })}
                max={30}
                label="Hero Slideshow Images"
                folder="images"
                testId="hero-carousel"
                autoFitHero
              />
              <p className="text-xs text-slate-500 mt-2">
                ✨ New uploads here are automatically resized to 2560 × 1080 (the perfect hero size). Existing images can be normalized in bulk via the button below.
              </p>

              <div className="flex items-start justify-between gap-3 mt-3 p-3 bg-purple-50 border border-purple-200 rounded-md">
                <div className="flex-1">
                  <p className="text-sm font-medium text-navy flex items-center gap-1.5">
                    <Sparkles className="w-4 h-4 text-purple-600" />
                    AI Clean &amp; Add Photo
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Drop a raw phone photo of a storefront — Gemini Nano Banana removes any cars/SUVs/vans, then auto-fits to 2560 × 1080. Takes ~10–15 seconds per image. Uses your Universal Key (~$0.04 per photo).
                  </p>
                </div>
                <HeroAICleanButton
                  disabled={(meta.carousel_images || []).length >= 30}
                  onAdd={(url) => setMeta({ ...meta, carousel_images: [...(meta.carousel_images || []), url] })}
                />
              </div>

              <div className="flex items-start justify-between gap-3 mt-3 p-3 bg-teal/5 border border-teal/20 rounded-md">
                <div className="flex-1">
                  <p className="text-sm font-medium text-navy">Auto-fit ALL images to 2560 × 1080</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Use this once to normalize the existing carousel. New uploads are auto-fit on upload, so you only need to click this when batch-importing or fixing legacy images. Originals are preserved as separate files.
                  </p>
                </div>
                <HeroAutoFitButton />
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Tip: position 1 is preferred for SEO/LCP — keep your strongest shot first.
              </p>
            </div>
          )}
          {lt === "benefits" && section?.section_name === "benefits" && section?.page_slug === "home" && (
            <div className="border-t pt-3"><Label className="text-sm font-semibold">Highlight Stat</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Input placeholder="Value (e.g. +35%)" value={meta.highlight_stat?.value || ""} onChange={(e) => setMeta({ ...meta, highlight_stat: { ...meta.highlight_stat, value: e.target.value } })} data-testid="highlight-stat-value" />
                <Input placeholder="Label (e.g. Avg. Revenue Growth)" value={meta.highlight_stat?.label || ""} onChange={(e) => setMeta({ ...meta, highlight_stat: { ...meta.highlight_stat, label: e.target.value } })} data-testid="highlight-stat-label" />
              </div>
              <div className="mt-4">
                <Label className="text-sm font-semibold mb-2 block">Section Video</Label>
                <VideoUpload value={meta.video_url || ""} onChange={(url) => setMeta({ ...meta, video_url: url })} label="Advantage Video" folder="videos" testId="benefits-video" />
                <p className="text-xs text-slate-500 mt-1">Upload an MP4 video to display in the Advantage section.</p>
              </div>
            </div>
          )}
          <FontColorEditor colors={meta.colors || {}} onChange={(c) => setMeta({ ...meta, colors: c })} />
          <div className="flex items-center gap-3 pt-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Active (visible on page)</Label></div>
          <Button onClick={handleSave} disabled={submitting} className="w-full bg-teal hover:bg-teal/90" data-testid="save-section-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save Changes</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ===== Generic Section Editor =====
function GenericSectionDialog({ section, open, onClose, onSaved, isNew, pageSlug }) {
  const [form, setForm] = useState({ page_slug: "", section_name: "", title: "", content: "", image_url: "", video_url: "", button_text: "", button_link: "", order: 0, active: true });
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    if (section) setForm({ page_slug: section.page_slug || pageSlug || "", section_name: section.section_name || "", title: section.title || "", content: section.content || "", image_url: section.image_url || "", video_url: section.video_url || "", button_text: section.button_text || "", button_link: section.button_link || "", order: section.order || 0, active: section.active !== false });
    else if (isNew) setForm({ page_slug: pageSlug || "", section_name: "", title: "", content: "", image_url: "", video_url: "", button_text: "", button_link: "", order: 0, active: true });
  }, [section, isNew, pageSlug]);
  const handleSave = async () => {
    if (!form.section_name.trim()) return toast.error("Section name is required");
    setSubmitting(true);
    try {
      if (isNew) { await createPageContent(form); toast.success("Section created"); }
      else { await updatePageContent(section.id, form); toast.success("Section updated"); }
      onSaved(); onClose();
    } catch (e) { toast.error("Error saving"); }
    finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isNew ? "Add Content Section" : "Edit Content Section"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Section Name</Label><Input value={form.section_name} onChange={(e) => setForm({ ...form, section_name: e.target.value })} disabled={!isNew} /></div>
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
          <div><Label>Content</Label><Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={4} /></div>
          <div><Label>Image URL</Label><Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Button Text</Label><Input value={form.button_text} onChange={(e) => setForm({ ...form, button_text: e.target.value })} /></div>
            <div><Label>Button Link</Label><Input value={form.button_link} onChange={(e) => setForm({ ...form, button_link: e.target.value })} /></div>
          </div>
          <div className="flex items-center gap-3"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Active</Label></div>
          <Button onClick={handleSave} disabled={submitting} className="w-full bg-teal hover:bg-teal/90" data-testid="save-generic-section-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : isNew ? "Add Section" : "Save Changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ===== FAQ Icon Options =====
const FAQ_ICON_OPTIONS = [
  { value: "HelpCircle", label: "Help", Icon: HelpCircle },
  { value: "CreditCard", label: "Credit Card", Icon: CreditCard },
  { value: "Smartphone", label: "Phone", Icon: Smartphone },
  { value: "Wifi", label: "Wifi", Icon: Wifi },
  { value: "Shield", label: "Shield", Icon: Shield },
  { value: "Settings", label: "Settings", Icon: Settings },
  { value: "Users", label: "Users", Icon: Users },
  { value: "MapPin", label: "Location", Icon: MapPin },
  { value: "FileText", label: "Document", Icon: FileText },
  { value: "Phone", label: "Call", Icon: Phone },
  { value: "Zap", label: "Lightning", Icon: Zap },
  { value: "Globe", label: "Globe", Icon: Globe },
  { value: "Package", label: "Package", Icon: Package },
  { value: "Headphones", label: "Support", Icon: Headphones },
  { value: "BarChart3", label: "Chart", Icon: BarChart3 },
  { value: "Lock", label: "Lock", Icon: Lock },
  { value: "Star", label: "Star", Icon: Star },
  { value: "Truck", label: "Shipping", Icon: Truck },
  { value: "MessageCircle", label: "Chat", Icon: MessageCircle },
];
const FAQ_ICON_MAP = Object.fromEntries(FAQ_ICON_OPTIONS.map((o) => [o.value, o.Icon]));

// ===== FAQ Management Panel (embedded in Pages tab) =====
function FaqManagementPanel() {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCat, setExpandedCat] = useState(null);
  const [catDialog, setCatDialog] = useState(false);
  const [itemDialog, setItemDialog] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [catForm, setCatForm] = useState({ name: "", icon: "HelpCircle", order: 0, active: true });
  const [itemForm, setItemForm] = useState({ category_id: "", question: "", answer: "", order: 0, active: true });

  const loadData = async () => {
    try {
      const [cats, faqs] = await Promise.all([getFaqCategories(), getFaqItems()]);
      setCategories(cats);
      setItems(faqs);
    } catch (e) { toast.error("Failed to load FAQ data"); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const openCatDialog = (cat = null) => {
    if (cat) { setEditingCat(cat); setCatForm({ name: cat.name, icon: cat.icon, order: cat.order, active: cat.active }); }
    else { setEditingCat(null); setCatForm({ name: "", icon: "HelpCircle", order: categories.length, active: true }); }
    setCatDialog(true);
  };

  const saveCat = async () => {
    if (!catForm.name.trim()) { toast.error("Category name required"); return; }
    setSaving(true);
    try {
      if (editingCat) { await updateFaqCategory(editingCat.id, catForm); toast.success("Category updated"); }
      else { await createFaqCategory(catForm); toast.success("Category created"); }
      setCatDialog(false); loadData();
    } catch (e) { toast.error("Failed to save category"); }
    finally { setSaving(false); }
  };

  const removeCat = async (catId) => {
    if (!window.confirm("Delete this category and all its FAQs?")) return;
    try { await deleteFaqCategory(catId); toast.success("Category deleted"); loadData(); }
    catch (e) { toast.error("Failed to delete"); }
  };

  const openItemDialog = (catId, item = null) => {
    if (item) { setEditingItem(item); setItemForm({ category_id: item.category_id, question: item.question, answer: item.answer, order: item.order, active: item.active }); }
    else { setEditingItem(null); const catItems = items.filter((i) => i.category_id === catId); setItemForm({ category_id: catId, question: "", answer: "", order: catItems.length, active: true }); }
    setItemDialog(true);
  };

  const saveItem = async () => {
    if (!itemForm.question.trim() || !itemForm.answer.trim()) { toast.error("Question and answer required"); return; }
    setSaving(true);
    try {
      if (editingItem) { await updateFaqItem(editingItem.id, itemForm); toast.success("FAQ updated"); }
      else { await createFaqItem(itemForm); toast.success("FAQ created"); }
      setItemDialog(false); loadData();
    } catch (e) { toast.error("Failed to save FAQ"); }
    finally { setSaving(false); }
  };

  const removeItem = async (itemId) => {
    if (!window.confirm("Delete this FAQ?")) return;
    try { await deleteFaqItem(itemId); toast.success("FAQ deleted"); loadData(); }
    catch (e) { toast.error("Failed to delete"); }
  };

  const toggleItemActive = async (item) => {
    try { await updateFaqItem(item.id, { active: !item.active }); loadData(); }
    catch (e) { toast.error("Failed to update"); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-teal" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-faqs-panel">
      <Card className="border-slate-200 mb-4">
        <CardContent className="p-4 flex items-center justify-between">
          <div><h3 className="font-semibold text-navy">FAQs</h3><p className="text-sm text-slate-500">{categories.length} categories, {items.length} questions</p></div>
          <div className="flex items-center gap-2">
            <a href="/faqs" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-teal hover:text-teal/80 text-sm"><Eye className="w-4 h-4" /> Preview</a>
            <Button onClick={() => openCatDialog()} className="bg-teal hover:bg-teal/90" data-testid="add-category-btn"><FolderPlus className="w-4 h-4 mr-2" /> Add Category</Button>
          </div>
        </CardContent>
      </Card>

      {categories.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-xl">
          <HelpCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 mb-4">No FAQ categories yet</p>
          <Button onClick={() => openCatDialog()} className="bg-teal hover:bg-teal/90"><FolderPlus className="w-4 h-4 mr-2" /> Create First Category</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {categories.map((cat) => {
            const catItems = items.filter((i) => i.category_id === cat.id);
            const isExpanded = expandedCat === cat.id;
            const CatIcon = FAQ_ICON_MAP[cat.icon] || HelpCircle;
            return (
              <Card key={cat.id} className="border-slate-200 overflow-hidden" data-testid={`faq-cat-${cat.id}`}>
                <div className="flex items-center gap-3 p-4 hover:bg-slate-50 transition-colors">
                  <button onClick={() => setExpandedCat(isExpanded ? null : cat.id)} className="flex items-center gap-3 flex-1 text-left" data-testid={`expand-cat-${cat.id}`}>
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                    <div className="w-8 h-8 bg-teal/10 rounded-lg flex items-center justify-center"><CatIcon className="w-4 h-4 text-teal" /></div>
                    <div>
                      <span className="font-semibold text-navy">{cat.name}</span>
                      <span className="text-xs text-slate-400 ml-2">({catItems.length} questions)</span>
                    </div>
                  </button>
                  <div className="flex items-center gap-1">
                    {!cat.active && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Hidden</span>}
                    <Button variant="ghost" size="sm" onClick={() => openItemDialog(cat.id)} data-testid={`add-faq-${cat.id}`}><Plus className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => openCatDialog(cat)} data-testid={`edit-cat-${cat.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => removeCat(cat.id)} className="text-red-500 hover:text-red-600" data-testid={`delete-cat-${cat.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
                {isExpanded && (
                  <div className="border-t border-slate-100">
                    {catItems.length === 0 ? (
                      <div className="p-6 text-center text-slate-400 text-sm">No questions yet. <button onClick={() => openItemDialog(cat.id)} className="text-teal hover:underline">Add one</button></div>
                    ) : (
                      <div className="divide-y divide-slate-100">
                        {catItems.map((item) => (
                          <div key={item.id} className="flex items-start gap-3 p-4 pl-14 hover:bg-slate-50" data-testid={`faq-item-${item.id}`}>
                            <GripVertical className="w-4 h-4 text-slate-300 mt-1 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className={`font-medium text-sm ${item.active ? "text-navy" : "text-slate-400"}`}>{item.question}</p>
                              <p className="text-xs text-slate-400 mt-1 line-clamp-1">{item.answer}</p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Switch checked={item.active} onCheckedChange={() => toggleItemActive(item)} />
                              <Button variant="ghost" size="sm" onClick={() => openItemDialog(cat.id, item)} data-testid={`edit-faq-${item.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                              <Button variant="ghost" size="sm" onClick={() => removeItem(item.id)} className="text-red-500 hover:text-red-600" data-testid={`delete-faq-${item.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="p-3 pl-14 border-t border-slate-100">
                      <Button variant="outline" size="sm" onClick={() => openItemDialog(cat.id)} className="text-teal border-teal/30" data-testid={`add-faq-bottom-${cat.id}`}><Plus className="w-3.5 h-3.5 mr-1" /> Add Question</Button>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Category Dialog */}
      <Dialog open={catDialog} onOpenChange={setCatDialog}>
        <DialogContent data-testid="cat-dialog">
          <DialogHeader><DialogTitle>{editingCat ? "Edit Category" : "Add Category"}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div><Label className="text-sm text-slate-600 mb-1 block">Category Name</Label><Input value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} placeholder="e.g. Billing & Payments" data-testid="cat-name-input" /></div>
            <div>
              <Label className="text-sm text-slate-600 mb-1 block">Icon</Label>
              <Select value={catForm.icon} onValueChange={(v) => setCatForm({ ...catForm, icon: v })}>
                <SelectTrigger data-testid="cat-icon-select"><SelectValue /></SelectTrigger>
                <SelectContent>{FAQ_ICON_OPTIONS.map((opt) => { const I = opt.Icon; return (<SelectItem key={opt.value} value={opt.value}><span className="flex items-center gap-2"><I className="w-4 h-4" /> {opt.label}</span></SelectItem>); })}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3"><Label className="text-sm text-slate-600">Order</Label><Input type="number" value={catForm.order} onChange={(e) => setCatForm({ ...catForm, order: parseInt(e.target.value) || 0 })} className="w-20" /></div>
            <div className="flex items-center gap-3"><Switch checked={catForm.active} onCheckedChange={(v) => setCatForm({ ...catForm, active: v })} /><Label className="text-sm text-slate-600">Visible on website</Label></div>
            <div className="flex gap-2 pt-2">
              <Button onClick={saveCat} disabled={saving} className="bg-teal hover:bg-teal/90 flex-1" data-testid="cat-save-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}{editingCat ? "Update" : "Create"} Category</Button>
              <Button variant="outline" onClick={() => setCatDialog(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* FAQ Item Dialog */}
      <Dialog open={itemDialog} onOpenChange={setItemDialog}>
        <DialogContent className="max-w-2xl" data-testid="item-dialog">
          <DialogHeader><DialogTitle>{editingItem ? "Edit FAQ" : "Add FAQ"}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-sm text-slate-600 mb-1 block">Category</Label>
              <Select value={itemForm.category_id} onValueChange={(v) => setItemForm({ ...itemForm, category_id: v })}>
                <SelectTrigger data-testid="item-cat-select"><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map((cat) => (<SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-sm text-slate-600 mb-1 block">Question</Label><Input value={itemForm.question} onChange={(e) => setItemForm({ ...itemForm, question: e.target.value })} placeholder="What is...?" data-testid="item-question-input" /></div>
            <div><Label className="text-sm text-slate-600 mb-1 block">Answer</Label><Textarea value={itemForm.answer} onChange={(e) => setItemForm({ ...itemForm, answer: e.target.value })} placeholder="Write the answer..." rows={6} data-testid="item-answer-input" /></div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-3"><Label className="text-sm text-slate-600">Order</Label><Input type="number" value={itemForm.order} onChange={(e) => setItemForm({ ...itemForm, order: parseInt(e.target.value) || 0 })} className="w-20" /></div>
              <div className="flex items-center gap-3"><Switch checked={itemForm.active} onCheckedChange={(v) => setItemForm({ ...itemForm, active: v })} /><Label className="text-sm text-slate-600">Active</Label></div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={saveItem} disabled={saving} className="bg-teal hover:bg-teal/90 flex-1" data-testid="item-save-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}{editingItem ? "Update" : "Add"} FAQ</Button>
              <Button variant="outline" onClick={() => setItemDialog(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===== Career Positions Management Panel =====
const POSITION_TYPES = [
  { value: "sales_consultant", label: "Sales Consultant" },
  { value: "sales_rep_pt", label: "Sales Consultant (Part-Time)" },
  { value: "assistant_manager", label: "Assistant Manager" },
  { value: "store_manager", label: "Store Manager" },
  { value: "custom", label: "Custom" },
];

function CareerPositionsPanel() {
  const [positions, setPositions] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [posDialog, setPosDialog] = useState(false);
  const [editingPos, setEditingPos] = useState(null);
  const [saving, setSaving] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [filterLoc, setFilterLoc] = useState("all");
  const [bulkEditDialog, setBulkEditDialog] = useState(false);
  const [bulkEditType, setBulkEditType] = useState(null);
  const [bulkEditForm, setBulkEditForm] = useState({ description: "", responsibilities: "", requirements: "", benefits: "" });
  const [savingBulkEdit, setSavingBulkEdit] = useState(false);
  const [posForm, setPosForm] = useState({
    title: "", location_id: "", position_type: "sales_consultant",
    description: "", responsibilities: "", requirements: "", benefits: "",
    salary_range: "", employment_type: "Full-time", active: true,
  });

  const loadData = async () => {
    try {
      const [pos, locs] = await Promise.all([getCareerPositions(), getLocations(true)]);
      setPositions(pos);
      setLocations(locs);
    } catch (e) { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const openPosDialog = (pos = null) => {
    if (pos) {
      setEditingPos(pos);
      setPosForm({
        title: pos.title, location_id: pos.location_id, position_type: pos.position_type,
        description: pos.description || "", responsibilities: (pos.responsibilities || []).join("\n"),
        requirements: (pos.requirements || []).join("\n"), benefits: (pos.benefits || []).join("\n"),
        salary_range: pos.salary_range || "", employment_type: pos.employment_type || "Full-time", active: pos.active,
      });
    } else {
      setEditingPos(null);
      setPosForm({
        title: "", location_id: locations[0]?.id || "", position_type: "sales_consultant",
        description: "", responsibilities: "", requirements: "", benefits: "",
        salary_range: "", employment_type: "Full-time", active: true,
      });
    }
    setPosDialog(true);
  };

  const savePos = async () => {
    if (!posForm.title.trim() || !posForm.location_id) { toast.error("Title and location required"); return; }
    setSaving(true);
    try {
      const data = {
        ...posForm,
        responsibilities: posForm.responsibilities.split("\n").filter(Boolean),
        requirements: posForm.requirements.split("\n").filter(Boolean),
        benefits: posForm.benefits.split("\n").filter(Boolean),
      };
      if (editingPos) { await updateCareerPosition(editingPos.id, data); toast.success("Position updated"); }
      else { await createCareerPosition(data); toast.success("Position created"); }
      setPosDialog(false); loadData();
    } catch (e) { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const removePos = async (id) => {
    if (!window.confirm("Delete this position?")) return;
    try { await deleteCareerPosition(id); toast.success("Deleted"); loadData(); }
    catch (e) { toast.error("Failed to delete"); }
  };

  const handleBulkGenerate = async () => {
    if (!window.confirm("This will generate Sales Consultant, Assistant Manager, Store Manager, and Part-Time Sales Rep positions for ALL active locations (skipping duplicates). Continue?")) return;
    setBulkLoading(true);
    try {
      const res = await bulkGeneratePositions();
      toast.success(res.message);
      loadData();
    } catch (e) { toast.error("Bulk generation failed"); }
    finally { setBulkLoading(false); }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm("Are you sure you want to DELETE ALL job positions? This cannot be undone.")) return;
    if (!window.confirm("This will remove all " + positions.length + " positions. Are you absolutely sure?")) return;
    setBulkLoading(true);
    try {
      const res = await deleteAllCareerPositions();
      toast.success(res.message);
      loadData();
    } catch (e) { toast.error("Delete failed"); }
    finally { setBulkLoading(false); }
  };

  const openBulkEdit = (typeKey) => {
    const typePositions = positions.filter((p) => p.position_type === typeKey);
    const sample = typePositions[0] || {};
    setBulkEditType(typeKey);
    setBulkEditForm({
      description: sample.description || "",
      responsibilities: (sample.responsibilities || []).join("\n"),
      requirements: (sample.requirements || []).join("\n"),
      benefits: (sample.benefits || []).join("\n"),
    });
    setBulkEditDialog(true);
  };

  const saveBulkEdit = async () => {
    if (!bulkEditType) return;
    setSavingBulkEdit(true);
    try {
      const data = {
        position_type: bulkEditType,
        description: bulkEditForm.description,
        responsibilities: bulkEditForm.responsibilities.split("\n").filter(Boolean),
        requirements: bulkEditForm.requirements.split("\n").filter(Boolean),
        benefits: bulkEditForm.benefits.split("\n").filter(Boolean),
      };
      const res = await bulkUpdatePositionsByType(data);
      toast.success(res.message);
      setBulkEditDialog(false);
      loadData();
    } catch (e) { toast.error("Bulk update failed"); }
    finally { setSavingBulkEdit(false); }
  };

  const filtered = positions.filter((p) => {
    if (filterType !== "all" && p.position_type !== filterType) return false;
    if (filterLoc !== "all" && p.location_id !== filterLoc) return false;
    return true;
  });

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-teal" /></div>;

  return (
    <div className="space-y-4" data-testid="career-positions-panel">
      <Card className="border-slate-200">
        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div><h3 className="font-semibold text-navy">Job Positions</h3><p className="text-sm text-slate-500">{positions.length} positions across {new Set(positions.map((p) => p.location_id)).size} locations</p></div>
          <div className="flex gap-2 flex-wrap">
            <a href="/careers" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-teal hover:text-teal/80 text-sm"><Eye className="w-4 h-4" /> Preview</a>
            <Button variant="outline" size="sm" className="text-red-500 border-red-200 hover:bg-red-50" onClick={handleDeleteAll} disabled={bulkLoading || positions.length === 0} data-testid="delete-all-positions-btn">
              <Trash2 className="w-4 h-4 mr-1" /> Delete All
            </Button>
            <Button variant="outline" size="sm" onClick={handleBulkGenerate} disabled={bulkLoading} data-testid="bulk-generate-btn">
              {bulkLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />} Bulk Generate All
            </Button>
            <Button size="sm" className="bg-teal hover:bg-teal/90" onClick={() => openPosDialog()} data-testid="add-position-btn"><Plus className="w-4 h-4 mr-1" /> Add Position</Button>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Edit by Position Type */}
      <div className="flex gap-2 flex-wrap" data-testid="bulk-edit-buttons">
        <span className="text-xs text-slate-500 self-center mr-1">Bulk Edit:</span>
        {POSITION_TYPES.filter((t) => t.value !== "custom").map((t) => {
          const count = positions.filter((p) => p.position_type === t.value).length;
          return (
            <Button key={t.value} variant="outline" size="sm" className="h-7 text-xs" onClick={() => openBulkEdit(t.value)} disabled={count === 0} data-testid={`bulk-edit-${t.value}`}>
              <Edit className="w-3 h-3 mr-1" /> {t.label} ({count})
            </Button>
          );
        })}
      </div>

      <div className="flex gap-2 flex-wrap">
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="border border-slate-200 rounded-lg px-3 py-1.5 text-xs bg-white" data-testid="pos-filter-type">
          <option value="all">All Types</option>
          {POSITION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select value={filterLoc} onChange={(e) => setFilterLoc(e.target.value)} className="border border-slate-200 rounded-lg px-3 py-1.5 text-xs bg-white max-w-xs" data-testid="pos-filter-loc">
          <option value="all">All Locations</option>
          {locations.map((l) => <option key={l.id} value={l.id}>{l.name} — {l.city}, {l.state}</option>)}
        </select>
        <span className="text-xs text-slate-400 self-center">{filtered.length} results</span>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-xl">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 mb-3">No job positions yet</p>
          <Button onClick={handleBulkGenerate} disabled={bulkLoading} variant="outline">{bulkLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Bulk Generate for All Locations</Button>
        </div>
      ) : (
        <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
          {filtered.map((pos) => (
            <Card key={pos.id} className="border-slate-200" data-testid={`pos-card-${pos.id}`}>
              <CardContent className="p-3 flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span className="px-2 py-0.5 bg-teal/10 text-teal text-xs font-medium rounded">{POSITION_TYPES.find((t) => t.value === pos.position_type)?.label || pos.position_type}</span>
                    {!pos.active && <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs rounded">Inactive</span>}
                  </div>
                  <p className="font-medium text-navy text-sm">{pos.title}</p>
                  <p className="text-xs text-slate-400">{pos.location_name} — {pos.location_city}, {pos.location_state}</p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => openPosDialog(pos)} data-testid={`edit-pos-${pos.id}`}><Edit className="w-3 h-3 mr-1" /> Edit</Button>
                  <Button variant="ghost" size="sm" className="h-7 text-xs text-red-500" onClick={() => removePos(pos.id)} data-testid={`delete-pos-${pos.id}`}><Trash2 className="w-3 h-3" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Bulk Edit by Type Dialog */}
      <Dialog open={bulkEditDialog} onOpenChange={setBulkEditDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="bulk-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Bulk Edit: {POSITION_TYPES.find((t) => t.value === bulkEditType)?.label || bulkEditType}
            </DialogTitle>
            <p className="text-sm text-slate-500">Changes will apply to all {positions.filter((p) => p.position_type === bulkEditType).length} {POSITION_TYPES.find((t) => t.value === bulkEditType)?.label} positions</p>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div><Label className="text-sm">Description</Label><Textarea value={bulkEditForm.description} onChange={(e) => setBulkEditForm({ ...bulkEditForm, description: e.target.value })} rows={4} data-testid="bulk-desc" /></div>
            <div><Label className="text-sm">Responsibilities (one per line)</Label><Textarea value={bulkEditForm.responsibilities} onChange={(e) => setBulkEditForm({ ...bulkEditForm, responsibilities: e.target.value })} rows={5} data-testid="bulk-resp" /></div>
            <div><Label className="text-sm">Requirements (one per line)</Label><Textarea value={bulkEditForm.requirements} onChange={(e) => setBulkEditForm({ ...bulkEditForm, requirements: e.target.value })} rows={5} data-testid="bulk-req" /></div>
            <div><Label className="text-sm">Benefits (one per line)</Label><Textarea value={bulkEditForm.benefits} onChange={(e) => setBulkEditForm({ ...bulkEditForm, benefits: e.target.value })} rows={5} data-testid="bulk-ben" /></div>
            <div className="flex gap-2 pt-2">
              <Button onClick={saveBulkEdit} disabled={savingBulkEdit} className="bg-teal hover:bg-teal/90 flex-1" data-testid="bulk-edit-save">
                {savingBulkEdit ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Update All {POSITION_TYPES.find((t) => t.value === bulkEditType)?.label} Positions
              </Button>
              <Button variant="outline" onClick={() => setBulkEditDialog(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Position Dialog */}
      <Dialog open={posDialog} onOpenChange={setPosDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="pos-dialog">
          <DialogHeader><DialogTitle>{editingPos ? "Edit Position" : "Add Position"}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm">Title *</Label><Input value={posForm.title} onChange={(e) => setPosForm({ ...posForm, title: e.target.value })} placeholder="e.g. Sales Consultant" data-testid="pos-title-input" /></div>
              <div>
                <Label className="text-sm">Position Type *</Label>
                <select value={posForm.position_type} onChange={(e) => setPosForm({ ...posForm, position_type: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" data-testid="pos-type-select">
                  {POSITION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-sm">Location *</Label>
              <select value={posForm.location_id} onChange={(e) => setPosForm({ ...posForm, location_id: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" data-testid="pos-location-select">
                <option value="">Select location...</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name} — {l.city}, {l.state}</option>)}
              </select>
            </div>
            <div><Label className="text-sm">Description</Label><Textarea value={posForm.description} onChange={(e) => setPosForm({ ...posForm, description: e.target.value })} rows={3} data-testid="pos-desc-input" /></div>
            <div><Label className="text-sm">Responsibilities (one per line)</Label><Textarea value={posForm.responsibilities} onChange={(e) => setPosForm({ ...posForm, responsibilities: e.target.value })} rows={4} placeholder="Achieve sales goals&#10;Provide customer service..." data-testid="pos-resp-input" /></div>
            <div><Label className="text-sm">Requirements (one per line)</Label><Textarea value={posForm.requirements} onChange={(e) => setPosForm({ ...posForm, requirements: e.target.value })} rows={4} placeholder="High school diploma&#10;1+ years experience..." data-testid="pos-req-input" /></div>
            <div><Label className="text-sm">Benefits (one per line)</Label><Textarea value={posForm.benefits} onChange={(e) => setPosForm({ ...posForm, benefits: e.target.value })} rows={4} placeholder="Competitive pay&#10;Health insurance..." data-testid="pos-ben-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm">Salary Range</Label><Input value={posForm.salary_range} onChange={(e) => setPosForm({ ...posForm, salary_range: e.target.value })} placeholder="$30,000 - $55,000/year" data-testid="pos-salary-input" /></div>
              <div><Label className="text-sm">Employment Type</Label><Input value={posForm.employment_type} onChange={(e) => setPosForm({ ...posForm, employment_type: e.target.value })} data-testid="pos-emp-type-input" /></div>
            </div>
            <div className="flex items-center gap-3"><Switch checked={posForm.active} onCheckedChange={(v) => setPosForm({ ...posForm, active: v })} /><Label className="text-sm">Active (visible on careers page)</Label></div>
            <div className="flex gap-2 pt-2">
              <Button onClick={savePos} disabled={saving} className="bg-teal hover:bg-teal/90 flex-1" data-testid="pos-save-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}{editingPos ? "Update" : "Create"} Position</Button>
              <Button variant="outline" onClick={() => setPosDialog(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===== Main Component =====
export default function AdminPages({ embedded = false }) {
  const [pages, setPages] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("home");
  const [editSection, setEditSection] = useState(null);
  const [editSectionDef, setEditSectionDef] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [genericSection, setGenericSection] = useState(null);
  const [genericOpen, setGenericOpen] = useState(false);
  const [genericIsNew, setGenericIsNew] = useState(false);

  const fetchPages = useCallback(async () => {
    try { setPages(await getAllPages()); } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPages(); }, [fetchPages]);

  const openStructuredEdit = (section) => {
    const defs = PAGE_SECTIONS[activeTab];
    const def = defs ? defs[section.section_name] : null;
    setEditSection(section); setEditSectionDef(def); setEditDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this section?")) return;
    try { await deletePageContent(id); toast.success("Deleted"); fetchPages(); } catch (e) { toast.error("Error"); }
  };

  const handleMove = async (slug, idx, direction) => {
    const sections = pages[slug] || [];
    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= sections.length) return;
    const a = sections[idx];
    const b = sections[targetIdx];
    try {
      // Use index-based ordering to avoid duplicate order values
      await Promise.all([
        updatePageContent(a.id, { order: targetIdx }),
        updatePageContent(b.id, { order: idx }),
      ]);
      toast.success("Section moved");
      fetchPages();
    } catch (e) { toast.error("Error moving section"); }
  };

  const handleDuplicate = async (section) => {
    try {
      const copy = {
        page_slug: section.page_slug,
        section_name: section.section_name + "_copy_" + Date.now(),
        title: (section.title || "") + " (Copy)",
        content: section.content || "",
        image_url: section.image_url || "",
        button_text: section.button_text || "",
        button_link: section.button_link || "",
        metadata: section.metadata ? JSON.parse(JSON.stringify(section.metadata)) : null,
        order: (section.order || 0) + 1,
        active: section.active !== false,
      };
      await createPageContent(copy);
      toast.success("Section duplicated");
      fetchPages();
    } catch (e) { toast.error("Error duplicating section"); }
  };

  const hasSectionDefs = !!PAGE_SECTIONS[activeTab];

  const content = (
      <div className="space-y-6" data-testid="admin-pages">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2 text-sm text-slate-500"><Layout className="w-4 h-4" /> Edit website page content</div>
          {!hasSectionDefs && activeTab !== "faqs" && (
            <Button onClick={() => { setGenericSection(null); setGenericIsNew(true); setGenericOpen(true); }} className="bg-teal hover:bg-teal/90" data-testid="add-content-btn"><Plus className="w-4 h-4 mr-2" /> Add Section</Button>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-slate-200 p-1 h-auto flex-wrap">
            {pagesList.map((page) => (
              <TabsTrigger key={page.slug} value={page.slug} className="data-[state=active]:bg-teal data-[state=active]:text-white" data-testid={`page-tab-${page.slug}`}>{page.name}</TabsTrigger>
            ))}
          </TabsList>

          {pagesList.map((page) => {
            // FAQs tab uses dedicated FAQ management, not CMS sections
            if (page.slug === "faqs") {
              return (
                <TabsContent key={page.slug} value={page.slug} className="mt-6">
                  <FaqManagementPanel />
                </TabsContent>
              );
            }
            // Careers tab shows CMS sections + Job Positions management
            if (page.slug === "careers") {
              const defs = PAGE_SECTIONS[page.slug];
              return (
                <TabsContent key={page.slug} value={page.slug} className="mt-6 space-y-8">
                  {/* CMS Hero/Benefits/CTA sections */}
                  <div>
                    <Card className="border-slate-200 mb-4">
                      <CardContent className="p-4 flex items-center justify-between">
                        <div><h3 className="font-semibold text-navy">Careers Page Content</h3><p className="text-sm text-slate-500">{(pages[page.slug] || []).length} CMS sections</p></div>
                        <a href="/careers" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-teal hover:text-teal/80 text-sm"><Eye className="w-4 h-4" /> Preview</a>
                      </CardContent>
                    </Card>
                    {(pages[page.slug] || []).length > 0 && (
                      <div className="space-y-3">
                        {(pages[page.slug] || []).map((section, idx) => {
                          const sectionDef = defs ? defs[section.section_name] : null;
                          const sectionLabel = sectionDef?.label || section.section_name;
                          return (
                            <Card key={section.id} className="border-slate-200 hover:border-slate-300 transition-colors" data-testid={`section-card-${section.section_name}`}>
                              <CardContent className="p-4 flex items-center justify-between">
                                <div>
                                  <span className="px-2 py-0.5 bg-teal/10 text-teal text-xs font-medium rounded">{sectionLabel}</span>
                                  {section.title && <p className="font-medium text-navy text-sm mt-1">{section.title}</p>}
                                </div>
                                <Button size="sm" variant="outline" className="h-8" onClick={() => sectionDef ? openStructuredEdit(section) : (() => { setGenericSection(section); setGenericIsNew(false); setGenericOpen(true); })()}><Edit className="w-3 h-3 mr-1" /> Edit</Button>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {/* Job Positions Management */}
                  <CareerPositionsPanel />
                </TabsContent>
              );
            }
            const defs = PAGE_SECTIONS[page.slug];
            return (
              <TabsContent key={page.slug} value={page.slug} className="mt-6">
                <Card className="border-slate-200 mb-4">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div><h3 className="font-semibold text-navy">{page.name}</h3><p className="text-sm text-slate-500">{(pages[page.slug] || []).length} sections</p></div>
                    <a href={page.slug === "home" ? "/" : `/${page.slug}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-teal hover:text-teal/80 text-sm"><Eye className="w-4 h-4" /> Preview</a>
                  </CardContent>
                </Card>

                {loading ? (
                  <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-teal" /></div>
                ) : (pages[page.slug] || []).length === 0 ? (
                  <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-xl">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No content sections yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {(pages[page.slug] || []).map((section, idx) => {
                      const sectionDef = defs ? defs[section.section_name] : null;
                      const sectionLabel = sectionDef?.label || section.section_name;
                      const listItems = section.metadata?.items || [];
                      return (
                        <Card key={section.id} className="border-slate-200 hover:border-slate-300 transition-colors" data-testid={`section-card-${section.section_name}`}>
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <span className="px-2 py-0.5 bg-teal/10 text-teal text-xs font-medium rounded">{sectionLabel}</span>
                                  {!section.active && <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs rounded">Hidden</span>}
                                </div>
                                {section.title && <p className="font-medium text-navy text-sm mt-1">{section.title}</p>}
                                {section.content && <p className="text-slate-500 text-xs mt-0.5 line-clamp-2">{section.content}</p>}
                                {sectionDef?.listType === "plans" && (
                                  <div className="mt-2 flex flex-wrap gap-1">
                                    {(section.metadata?.items || []).length > 0 && <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">Phone: {section.metadata.items.length}</span>}
                                    {(section.metadata?.tablet_items || []).length > 0 && <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">Tablet: {section.metadata.tablet_items.length}</span>}
                                    {(section.metadata?.home_internet_items || []).length > 0 && <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">Home Internet: {section.metadata.home_internet_items.length}</span>}
                                    {(section.metadata?.hotspot_items || []).length > 0 && <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">Hotspot: {section.metadata.hotspot_items.length}</span>}
                                  </div>
                                )}
                                {sectionDef?.listType !== "plans" && listItems.length > 0 && (
                                  <div className="mt-2 flex flex-wrap gap-1">
                                    {listItems.slice(0, 6).map((item, i) => (
                                      <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                                        {typeof item === "string" ? item.slice(0, 30) : (item.title || item.label || item.name || item.value || `Item ${i + 1}`)}
                                      </span>
                                    ))}
                                    {listItems.length > 6 && <span className="text-xs text-slate-400">+{listItems.length - 6} more</span>}
                                  </div>
                                )}
                              </div>
                              <div className="flex gap-1 items-center">
                                <div className="flex flex-col gap-0.5 mr-1">
                                  <Button size="icon" variant="ghost" className="h-6 w-6 text-slate-400 hover:text-slate-700" onClick={() => handleMove(page.slug, idx, -1)} disabled={idx === 0} title="Move up" data-testid={`move-up-${section.section_name}`}><ArrowUp className="w-3 h-3" /></Button>
                                  <Button size="icon" variant="ghost" className="h-6 w-6 text-slate-400 hover:text-slate-700" onClick={() => handleMove(page.slug, idx, 1)} disabled={idx === (pages[page.slug] || []).length - 1} title="Move down" data-testid={`move-down-${section.section_name}`}><ArrowDown className="w-3 h-3" /></Button>
                                </div>
                                <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => handleDuplicate(section)} title="Duplicate section" data-testid={`duplicate-${section.section_name}`}><Copy className="w-3 h-3" /></Button>
                                <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => sectionDef ? openStructuredEdit(section) : (() => { setGenericSection(section); setGenericIsNew(false); setGenericOpen(true); })()} data-testid={`edit-section-${section.section_name}`}><Edit className="w-3 h-3" /> Edit</Button>
                                {!sectionDef && <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => handleDelete(section.id)}><Trash2 className="w-3 h-3" /></Button>}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </TabsContent>
            );
          })}
        </Tabs>

        {editSection && editSectionDef && <SectionEditorDialog section={editSection} sectionDef={editSectionDef} open={editDialogOpen} onClose={() => setEditDialogOpen(false)} onSaved={fetchPages} />}
        <GenericSectionDialog section={genericSection} open={genericOpen} onClose={() => setGenericOpen(false)} onSaved={fetchPages} isNew={genericIsNew} pageSlug={activeTab} />
      </div>
  );

  if (embedded) return content;
  return <AdminLayout title="Page Management" breadcrumb="Pages">{content}</AdminLayout>;
}

function HeroAutoFitButton() {
  const [running, setRunning] = useState(false);

  const handleClick = async () => {
    if (!window.confirm("Smart-resize ALL hero carousel images to 2560 × 1080? This processes them server-side (typically 10-30 seconds for 20+ images). Originals are kept on disk; carousel URLs are replaced with the resized versions.")) return;
    setRunning(true);
    try {
      const res = await autoFitHeroImages();
      toast.success(`Resized ${res.processed} of ${res.total} images${res.skipped ? ` (${res.skipped} skipped)` : ""}.`);
      // Force a full page reload so the live admin state + homepage cache are refreshed.
      setTimeout(() => window.location.reload(), 600);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-fit failed. Check backend logs.");
    } finally {
      setRunning(false);
    }
  };

  return (
    <Button
      size="sm"
      onClick={handleClick}
      disabled={running}
      className="bg-teal hover:bg-teal/90 text-white shrink-0"
      data-testid="hero-autofit-btn"
    >
      {running ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1.5" />}
      {running ? "Resizing..." : "Auto-fit All"}
    </Button>
  );
}

function HeroAICleanButton({ disabled, onAdd }) {
  const [running, setRunning] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    setRunning(true);
    const tid = toast.loading("Cleaning photo (removing cars + resizing)...");
    try {
      const res = await uploadCleanAndFitHero(file);
      const url = res.url.startsWith("http")
        ? res.url
        : `${process.env.REACT_APP_BACKEND_URL}${res.url}`;
      onAdd(url);
      toast.success("Photo cleaned & added to carousel", { id: tid });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI clean failed", { id: tid });
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        onClick={() => fileRef.current?.click()}
        disabled={running || disabled}
        className="bg-purple-600 hover:bg-purple-700 text-white shrink-0"
        data-testid="hero-ai-clean-btn"
      >
        {running ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
        {running ? "Processing..." : "AI Clean & Add"}
      </Button>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFile}
        data-testid="hero-ai-clean-file"
      />
    </>
  );
}


